import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { AsyncAction } from '../../../core/async-state/models/async-action.model';
import { handleErrors } from '../../../core/async-state/operators/handle-errors';
import * as AuthUINavigationActions from '../../../core/auth/actions/auth-ui-navigation.actions';
import { ModalsActions } from '../../../core/modals/actions/modals.actions';
import { RouterSelectors } from '../../../core/router/selectors/router.selectors';
import { BaseAppState } from '../../../core/store/reducers';
import { ToastService } from '../../../core/toasts/services/toast.service';
import {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  RenewCodeResponse,
  User,
} from '../../../shared/models';
import { USER_UI_PATH_ELEMENTS } from '../../users/definitions/user-ui-navigation.definitions';
import { AuthActions } from '../actions/auth.actions';
import { AuthService } from '../services/auth.service';

const RENEWED_CODE_TITLE = 'Code geändert';
const RENEWED_CODE_MESSAGE = 'Ihr Code wurde geändert.';

@Injectable()
export class AuthEffects {
  login$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.login),
        map((action) => action.payload),
        exhaustMap((auth: LoginRequest) =>
          this.apiService.login(auth.email, auth.password).pipe(
            handleErrors(() => AuthActions.loginFail()),
            // Success action gets the response data here while in other cases it gets the whole API Response
            map((response: ApiResponse<LoginResponse>) =>
              AuthActions.loginSuccess({ loginResponse: response.data })
            ),
            catchError((errorAction: AsyncAction) => of(errorAction))
          )
        )
      ),
    { dispatch: true }
  );

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess),
        tap(({ loginResponse }) => {
          const userLanguage = loginResponse.user.language;
          if (userLanguage) {
            this.translateService.use(userLanguage);
            this.store.dispatch(AuthActions.setUserLanguage({ language: userLanguage }));
          }
        })
      ),
    { dispatch: false }
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logout),
        switchMap(() => {
          return this.apiService.logout().pipe(
            handleErrors(() => AuthActions.logoutFail()),
            map(() => AuthActions.logoutSuccess()),
            catchError((errorAction: AsyncAction) => of(errorAction))
          );
        })
      ),
    { dispatch: true }
  );

  redirectAfterLogin$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess),
        withLatestFrom(this.store),
        tap(([_action, storeState]: [object & AsyncAction, BaseAppState]) => {
          const queryParams = RouterSelectors.getQueryParams(storeState);
          this.router.navigate([queryParams.returnUrl || USER_UI_PATH_ELEMENTS.rootElement]);
        })
      ),
    { dispatch: false }
  );

  redirectToLoginAfterSessionExpired$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.sessionExpired),
        map(() => AuthUINavigationActions.goToLogin())
      ),
    { dispatch: true }
  );

  redirectToLoginAfterLogout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logoutSuccess, AuthActions.logoutFail),
        map(() => AuthUINavigationActions.goToLogin())
      ),
    { dispatch: true }
  );

  showMessageAfterSuccessfulLogout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logoutSuccess),
        tap(() =>
          this.toastService.showSuccess(
            this.translateService.instant('toast.logout_success.title'),
            this.translateService.instant('toast.logout_success.message')
          )
        )
      ),
    { dispatch: false }
  );

  showMessageAfterSessionExpired$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.sessionExpired),
        tap(() =>
          this.toastService.showInfo(
            this.translateService.instant('toast.session_expired.title'),
            this.translateService.instant('toast.session_expired.message')
          )
        )
      ),
    { dispatch: false }
  );

  renewCode$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.renewCode),
        switchMap(() =>
          this.apiService.renewCode().pipe(
            handleErrors(() => AuthActions.renewCodeFail()),
            map((response: ApiResponse<RenewCodeResponse>) =>
              AuthActions.renewCodeSuccess({ code: response.data.code })
            ),
            catchError((errorAction: AsyncAction) => of(errorAction))
          )
        )
      ),
    { dispatch: true }
  );

  showSuccessMessageAfterRenewCode$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.renewCodeSuccess),
        tap(() => this.toastService.showInfo(RENEWED_CODE_TITLE, RENEWED_CODE_MESSAGE))
      ),
    { dispatch: false }
  );

  confirmTermsOfService$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.confirmTermsOfService),
        switchMap(() =>
          this.apiService.confirmTermsOfService().pipe(
            handleErrors(() => AuthActions.confirmTermsOfServiceUserFail()),
            map(() => AuthActions.confirmTermsOfServiceSuccess()),
            catchError((errorAction: AsyncAction) => of(errorAction))
          )
        )
      ),
    { dispatch: true }
  );

  closeModalOnConfirmSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.confirmTermsOfServiceSuccess),
        map(() => ModalsActions.closeDisclaimerModal())
      ),
    { dispatch: true }
  );

  updateUserLanguage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.updateUserLanguage),
        switchMap((action) =>
          this.apiService.updateUserLanguage(action.language).pipe(
            handleErrors(() => AuthActions.updateUserLanguageFail()),
            map((response: ApiResponse<{ user: User }>) =>
              AuthActions.updateUserLanguageSuccess({
                language: response.data.user.language!,
              })
            ),
            catchError((errorAction: AsyncAction) => of(errorAction))
          )
        )
      ),
    { dispatch: true }
  );

  adminLogin$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.adminLogin),
        map((a) => a.payload),
        exhaustMap(({ email, password }) =>
          this.apiService.adminLogin(email, password).pipe(
            handleErrors(() => AuthActions.adminLoginFail()),
            map(() => AuthActions.adminLoginSuccess()),
            catchError((errorAction: AsyncAction) => of(errorAction))
          )
        )
      ),
    { dispatch: true }
  );

  adminLoginSuccessNavigateToVerify$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.adminLoginSuccess),
        tap(() => {
          this.router.navigate([], {
            queryParams: { step: 'verify' },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          });
        })
      ),
    { dispatch: false }
  );

  adminLoginSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.adminLoginSuccess),
        tap(() =>
          this.toastService.showSuccess(
            this.translateService.instant('toast.admin_code_sent.title'),
            this.translateService.instant('toast.admin_code_sent.message')
          )
        )
      ),
    { dispatch: false }
  );

  verifyAdminCode$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.verifyAdminCode),
        map((a) => a.payload),
        exhaustMap(({ code }) =>
          this.apiService.verifyAdminCode(code).pipe(
            handleErrors(() => AuthActions.verifyAdminCodeFail()),
            mergeMap((response: ApiResponse<LoginResponse>) => [
              AuthActions.loginSuccess({ loginResponse: response.data }),
              AuthActions.verifyAdminCodeSuccess({ loginResponse: response.data }),
            ]),
            catchError((errorAction: AsyncAction) => of(errorAction))
          )
        )
      ),
    { dispatch: true }
  );

  constructor(
    private actions$: Actions,
    private store: Store<BaseAppState>,
    private router: Router,
    private apiService: AuthService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {}
}
